// Meta Pixel events.
//
// The base snippet in public/index.html loads fbq and calls init; it does NOT
// fire PageView, because this is a single-page app and the router fires that on
// every route change (including the first). Firing it in both places would
// double-count the landing page of every visit.
//
// Every call here is a no-op when fbq is missing - an ad blocker, a failed
// script load, a server-side render - and nothing in here is allowed to throw.
// Analytics must never be the reason a customer cannot book.

export const PIXEL_ID = '1057406793584621';

const call = (...args) => {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq(...args);
    }
  } catch (err) {
    // Deliberately swallowed. A tracking failure is not a user-facing failure.
  }
};

// Shared id for one real-world action, so the browser event and the server-side
// Conversions API event for the same purchase are counted once, not twice.
export const newEventId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (err) { /* fall through */ }
  return 'e' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
};

export const trackPageView = () => track('PageView');

// Every event carries an eventID. Meta also receives a server-side copy of
// browser events (a Conversions API mirror on Meta's side - our backend itself
// only sends Purchase), and a mirror copies whatever eventID the browser event
// had. Without one, Meta cannot tell the two copies are the same event: it
// flags "add an event_id parameter to your server payload" and risks counting
// each event twice. Pass eventId when both sides must agree on it (Purchase
// uses the shipment id, which our own Conversions API sends too); otherwise a
// fresh one is generated.
export const track = (event, params = {}, eventId) =>
  call('track', event, params, { eventID: eventId || newEventId() });

// --- the events this site reports -------------------------------------------

// A customer looked at rates for a real route. Sent with the cheapest quoted
// price so Meta has a value to optimise against even before a booking.
export const trackViewContent = ({ destination, weight, cheapest, currency = 'INR' } = {}) =>
  track('ViewContent', {
    content_type: 'product',
    content_category: 'shipping_quote',
    content_name: destination ? `Quote to ${destination}` : 'Shipping quote',
    ...(destination ? { destination } : {}),
    ...(weight ? { num_items: 1, weight } : {}),
    ...(cheapest ? { value: Number(cheapest), currency } : {}),
  });

// The customer has picked a carrier and opened the booking form.
// eventId should be the booking attempt's id, so a form that mounts twice for
// one attempt is still one InitiateCheckout.
export const trackInitiateCheckout = ({ carrier, destination, value, currency = 'INR', eventId } = {}) =>
  track('InitiateCheckout', {
    content_type: 'product',
    content_category: 'shipment',
    ...(carrier ? { content_name: carrier } : {}),
    ...(destination ? { destination } : {}),
    num_items: 1,
    ...(value ? { value: Number(value), currency } : {}),
  }, eventId);

export const trackCompleteRegistration = () =>
  track('CompleteRegistration', { status: true, content_name: 'Account created' });

// Only once a shipment is actually booked and paid for. eventId should be the
// shipment id, which is what the backend sends to the Conversions API, so the
// two reports of this purchase collapse into one.
export const trackPurchase = ({ value, currency = 'INR', carrier, shipmentNumber, eventId } = {}) =>
  track('Purchase', {
    value: Number(value || 0),
    currency,
    content_type: 'product',
    ...(carrier ? { content_name: carrier } : {}),
    ...(shipmentNumber ? { order_id: shipmentNumber } : {}),
    num_items: 1,
  }, eventId);
